<template>
  <div>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching Recipe Collection!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>
    <b-loading :is-full-page="true" v-model="isLoading"></b-loading>
    <Keypress key-event="keyup" :modifiers="['ctrlKey']" :key-code="83" :preventDefault="true" @success="save" />

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Recipe Collections"
      :currentTitle="title"
      :backRoute="{ name: 'RecipeCollectionListView' }"
    >
      <FeedsStatusBadge :status="recipeCollection.status" />
      <b-button outlined type="is-primary" size="is-small" icon-left="check" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" @click="save">Save</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ title }}</h1>
          <p class="page-subtitle">Recipe Collection Editor</p>
        </div>
      </div>

      <!-- Top Grid: General + Images -->
      <div class="top-grid">
        <FeedsCard title="General Information">
          <div class="form-group">
            <label class="form-label">Status</label>
            <b-select v-model="recipeCollection.status" placeholder="Status" expanded @input="save">
              <option>draft</option>
              <option>test</option>
              <option>fix</option>
              <option>live</option>
            </b-select>
          </div>
          <div class="form-group">
            <label class="form-label">Name</label>
            <b-input v-model="recipeCollection.title" @input="saveDebounced" />
          </div>
          <div class="form-group">
            <label class="form-label">Description</label>
            <b-input maxlength="100" type="textarea" v-model="recipeCollection.description" @input="saveDebounced"/>
          </div>
          <div class="form-group">
            <label class="form-label">Position</label>
            <b-numberinput
              v-model="recipeCollection.position"
              placeholder="e.g. 1"
              type="is-light"
              controls-position="compact"
              :min="1"
              @input="saveDebounced">
            </b-numberinput>
          </div>
        </FeedsCard>

        <FeedsCard title="Images">
          <template #header-actions>
            <b-button size="is-small" type="is-primary" icon-left="plus" @click="$refs.editImages.openAddImage()">Add Image</b-button>
          </template>
          <EditImages
            ref="editImages"
            v-model="recipeCollection.images"
            @input="save"
          />
        </FeedsCard>
      </div>

      <!-- Recipes Section -->
      <FeedsCard title="Recipes" class="mt-5">
        <EditRecipes
          :maxRecipes="20"
          :allowMultipleFreeRecipes="true"
          title=""
          v-model="recipeCollection.recipes"
          @input="save"
          @recipe-added="recipeAdded($event)"
          @recipe-removed="recipeRemoved($event)"
        />
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import EditImages from "@/components/EditImages"
import EditRecipes from "@/components/EditRecipes"
import db from "@/firebase/db"
import firebase from 'firebase/app'
import FeedsCard from "@/components/common/FeedsCard"
import FeedsStatusBadge from "@/components/common/FeedsStatusBadge"
import TopBar from "@/components/layout/TopBar"

export default {
  name: 'RecipeCollectionView',
  components: {
    EditImages,
    EditRecipes,
    FeedsCard,
    FeedsStatusBadge,
    TopBar,
    Keypress: () => import('vue-keypress'),
  },
  props: [
    'id'
  ],
  data() {
    return {
      isLoading: true,
      isError: false,
      recipeCollection: {}
    };
  },
  computed: {
    title() {
      return this.recipeCollection.title ?? "New Recipe Collection"
    },
    image() {
      if (this.recipeCollection.images == null) {
        return null;
      }
      return this.recipeCollection.images.length > 0 ? this.recipeCollection.images[0].src : null
    },
    displayRecipes() {
      var recipes = this.recipeCollection.recipes ?? {};
      var array = Object.keys(recipes).map(function(recipeID) {
        var newObj = {id: recipeID};
        for (var property in recipes[recipeID]) {
          newObj[property] = recipes[recipeID][property];
        }
        return newObj;
      });
      return array.sort((a, b) => a.position - b.position);
    }
  },
  created: function () {
    db.collection('recipe_collections')
      .doc(this.id)
      .get()
      .then(snapshot => {
        if (snapshot.exists) {
          this.recipeCollection = snapshot.data();
        } else {
          this.isError = true;
        }
        this.isLoading = false;
      })
      .catch((error) => {
        this.isError = true;
        this.$buefy.notification.open({type: 'is-danger', message: `Error fetching Recipe Collection: ${error}`})
      });
  },
  methods: {
    saveDebounced: debounce(function () {
      this.save()
    }, 1000),
    async saveAndClose(){
      this.save()
        .then(() => {
          this.$router.go(-1)
        })
    },
    async save(){
      db.collection('recipe_collections')
        .doc(this.id)
        .set(this.recipeCollection)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Recipe Collection successfully updated!`})
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving Recipe Collection: ${error}`})
        });
    },
    async recipeAdded(recipe){
      const entry = {};
      entry[`${this.id}`] = true;
      db.collection("recipe_recipe_collections").doc(recipe.id)
        .set(entry, {merge: true})
        .catch((error) => {
          console.error("Error adding recipe_recipe_collections entry: ", error);
        });
    },
    async recipeRemoved(recipe){
      const remove = {};
      remove[`${this.id}`] = firebase.firestore.FieldValue.delete();
      db.collection("recipe_recipe_collections").doc(recipe.id)
        .update(remove)
        .catch((error) => {
          console.error("Error removing recipe_recipe_collections entry: ", error);
        });
    },
  }
}
</script>

<style scoped>
.top-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  align-items: start;
}
</style>
